import { useEffect, useRef } from "react";
import {
  Bookmark,
  BookmarkCheck,
  Share2,
  Languages,
  Play,
  Brackets,
  X,
} from "lucide-react";

/**
 * AyahActionMenu — small popover triggered by a long-press on an ayah.
 *
 * Props:
 *   open: boolean
 *   onClose: () => void
 *   anchor: { x:number, y:number } — viewport coords where the menu should anchor
 *   isBookmarked: boolean — whether the target ayah is already bookmarked;
 *     toggles the label from "Bookmark" → "Remove bookmark" + filled icon
 *   onPlay, onBookmark, onShare, onTranslation, onSelectRange — callbacks
 *
 * The user picks ONE option, the menu closes, and the parent runs the action.
 * "Select range" arms range-selection mode in the parent; the next plain tap
 * on another ayah completes the range.
 */
export default function AyahActionMenu({
  open,
  onClose,
  anchor,
  isBookmarked = false,
  onPlay,
  onBookmark,
  onShare,
  onTranslation,
  onSelectRange,
}) {
  const menuRef = useRef(null);

  // Dismiss on Escape. Outside-pointer dismissal is now handled by the
  // transparent backdrop below (prevents click-through onto the ayah).
  useEffect(() => {
    if (!open) return;
    const onKey = (e) => {
      if (e.key === "Escape") onClose?.();
    };
    document.addEventListener("keydown", onKey);
    return () => {
      document.removeEventListener("keydown", onKey);
    };
  }, [open, onClose]);

  if (!open || !anchor) return null;

  // Clamp position so the menu never overflows the viewport.
  const MENU_W = 240;
  const MENU_H = 240;
  const margin = 12;
  const vw = typeof window !== "undefined" ? window.innerWidth : 1200;
  const vh = typeof window !== "undefined" ? window.innerHeight : 800;
  const left = Math.max(
    margin,
    Math.min(anchor.x - MENU_W / 2, vw - MENU_W - margin),
  );
  const top = Math.max(
    margin,
    Math.min(anchor.y + 12, vh - MENU_H - margin),
  );

  const items = [
    { key: "play", label: "Play", icon: Play, onClick: onPlay },
    {
      key: "bookmark",
      label: isBookmarked ? "Remove bookmark" : "Bookmark",
      icon: isBookmarked ? BookmarkCheck : Bookmark,
      onClick: onBookmark,
      iconClass: isBookmarked ? "text-gold-400 fill-gold-400" : "",
    },
    { key: "translation", label: "Translation", icon: Languages, onClick: onTranslation },
    { key: "share", label: "Share", icon: Share2, onClick: onShare },
    { key: "range", label: "Select range from here", icon: Brackets, onClick: onSelectRange },
  ];

  return (
    <>
      {/* Transparent backdrop — absorbs all pointer events so taps on
          anything outside the menu close it WITHOUT reaching the ayah
          underneath. Fixes the bug where menu buttons near an ayah were
          sometimes "clicked through" to the ayah behind. */}
      <div
        data-testid="ayah-action-menu-backdrop"
        onPointerDown={(e) => {
          e.stopPropagation();
          onClose?.();
        }}
        onClick={(e) => {
          // Belt-and-suspenders: click fires after pointerup on some platforms
          // and we don't want it to reach the ayah either.
          e.stopPropagation();
        }}
        style={{ position: "fixed", inset: 0, zIndex: 55, background: "transparent" }}
      />
      <div
        ref={menuRef}
        data-testid="ayah-action-menu"
        role="menu"
        style={{ position: "fixed", left, top, width: MENU_W, zIndex: 60 }}
        className="rounded-xl border border-white/10 bg-islamic-blue-900/95 backdrop-blur-md shadow-2xl py-1 font-outfit text-sm text-slate-200 select-none"
      >
        {items.map((it) => (
          <button
            key={it.key}
            data-testid={`ayah-action-${it.key}`}
            type="button"
            onPointerDown={(e) => e.stopPropagation()}
            onClick={(e) => {
              e.stopPropagation();
              it.onClick?.();
              onClose?.();
            }}
            className="w-full flex items-center gap-3 px-4 py-2.5 hover:bg-gold-400/15 hover:text-gold-300 transition-colors text-left"
          >
            <it.icon className={`w-4 h-4 ${it.iconClass || ""}`} />
            <span>{it.label}</span>
          </button>
        ))}
        <button
          data-testid="ayah-action-close"
          onPointerDown={(e) => e.stopPropagation()}
          onClick={(e) => {
            e.stopPropagation();
            onClose?.();
          }}
          className="w-full flex items-center justify-center gap-1 px-4 py-1.5 text-slate-500 hover:text-slate-300 text-xs border-t border-white/10 mt-1"
        >
          <X className="w-3 h-3" /> Cancel
        </button>
      </div>
    </>
  );
}
